import { Anchor, Card, Center, Group, Image, Stack, Text } from '@mantine/core';
import { type ReactNode, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  type BrowserLoginSuccess,
  fetchLoginMethods,
  type LOGIN_METHOD,
  type LoginChallenge,
  type LoginChallengeResult,
  type LoginChallengeUserResponse,
  type LoginMethodsResponse,
  type LoginRequest,
  type Verify2FALoginRequest,
} from '@/api';
import { DocumentTitle } from '@/components/Ui/DocumentTitle';
import { Login } from '../components/Login/Login';
import { ALWAYS_ON_METHODS, visibleMethods } from '../components/Login/login-methods';
import { useLanguageAwareRouting } from '../hooks/useLanguageAwareRouting';

export interface LoginPageProps {
  login: (loginRequest: LoginRequest) => Promise<LoginChallenge>;
  loginChallenge: (props: LoginChallengeUserResponse) => Promise<LoginChallengeResult>;
  verify2FA: (request: Verify2FALoginRequest) => Promise<BrowserLoginSuccess>;
  /** Backend root without `/v1`. */
  apiBaseUrl: string;

  mode?: 'signin' | 'signup'; // Default: 'signin'
  onLoginSuccess?: () => void; // Defaults to '/account'
  privacyRoute?: string; // Default: '/privacy'
  termsRoute?: string; // Default: '/terms'

  logo?: ReactNode; // Default: <img src="/logo.svg" />
  logoSrc?: string; // Default: '/logo.svg'
  brandName?: ReactNode;

  /** Restricts the methods the server offers; never adds any. */
  loginMethods?: LOGIN_METHOD[];
}

export function LoginPage(props: LoginPageProps) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [server, setServer] = useState<LoginMethodsResponse | null>(null);

  const {
    apiBaseUrl,
    mode = 'signin',
    onLoginSuccess,
    privacyRoute = '/privacy',
    termsRoute = '/terms',
    logoSrc = '/logo.svg',
  } = props;

  useEffect(() => {
    let ignore = false;
    fetchLoginMethods(apiBaseUrl.replace(/\/v1\/?$/, '')).then((res) => {
      if (!ignore) setServer(res);
    });
    return () => {
      ignore = true;
    };
  }, [apiBaseUrl]);

  const methods = visibleMethods(server?.methods ?? ALWAYS_ON_METHODS, props.loginMethods);

  const loginDoneCb = () => {
    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      nav(createLanguageURL('/account'));
    }
  };

  const logo = props.logo ?? <Image src={logoSrc} alt="Logo" w={48} h={48} fit="contain" />;

  return (
    <Center mih="100vh" py="xl">
      <DocumentTitle
        title={mode === 'signup' ? t('glob_navigation.getStarted') : t('login.signInLink')}
      />
      <Stack align="center" gap="lg" w="100%" maw={420} px="md">
        <Stack align="center" gap="xs">
          {logo}
          {props.brandName && (
            <Text fw={600} size="lg">
              {props.brandName}
            </Text>
          )}
        </Stack>

        <Card withBorder radius="md" p={{ base: 'md', sm: 'lg' }} w="100%" shadow="xs">
          <Login
            mode={mode}
            methods={methods}
            oidcDisplayName={server?.oidc_display_name}
            login={props.login}
            loginChallenge={props.loginChallenge}
            verify2FA={props.verify2FA}
            loginDoneCb={loginDoneCb}
          />
        </Card>

        <Stack align="center" gap="sm">
          <Text size="xs" c="dimmed" ta="center">
            <Trans
              i18nKey="login.legal"
              components={{
                terms: (
                  <Anchor component={Link} to={createLanguageURL(termsRoute)} inherit c="dimmed" />
                ),
                privacy: (
                  <Anchor
                    component={Link}
                    to={createLanguageURL(privacyRoute)}
                    inherit
                    c="dimmed"
                  />
                ),
              }}
            />
          </Text>
          {mode === 'signup' && (
            <Group gap={6} justify="center">
              <Text size="sm" c="dimmed">
                {t('login.alreadyHaveAccount')}
              </Text>
              <Anchor component={Link} to={createLanguageURL('/login')} size="sm" fw={500}>
                {t('login.signInLink')}
              </Anchor>
            </Group>
          )}
        </Stack>
      </Stack>
    </Center>
  );
}
